import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Users, 
  Eye, 
  RefreshCw, 
  Download,
  Settings,
  AlertTriangle,
  CheckCircle,
  Clock,
  MessageSquare,
  X
} from 'lucide-react';
import apiClient from '../services/apiClient';
import StatusBadge from '../components/StatusBadge';
import LastEditInfo from '../components/LastEditInfo';

/**
 * TeacherMonitorWeekPage - Sprint 2
 * 
 * Consola Docente Básica - Monitor de Semana Específica
 * 
 * Funcionalidades Sprint 2:
 * - Vista general de todos los grupos y su progreso en una semana
 * - Estado de submissions por grupo
 * - Identificar quién no ha comentado
 * - Ver contenido de B2 por grupo
 * - Reabrir submissions declarados
 * 
 * Route: /teacher/:sectionId/week/:weekNumber
 * 
 * IMPORTANTE: Esta página se usa CON DashboardLayout
 */

const TeacherMonitorWeekPage = () => {
  const { sectionId, weekNumber } = useParams();
  const navigate = useNavigate();
  const weekNum = parseInt(weekNumber, 10);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  
  const [section, setSection] = useState(null);
  const [courseDefinition, setCourseDefinition] = useState(null);
  const [weekData, setWeekData] = useState(null);
  const [groups, setGroups] = useState([]);
  const [submissionsData, setSubmissionsData] = useState([]);
  
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [groupDetails, setGroupDetails] = useState(null);
  
  useEffect(() => {
    loadWeekMonitorData();
  }, [sectionId, weekNum]);
  
  const loadWeekMonitorData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // 1. Obtener información de la sección (sistema CourseSection)
      const sectionResponse = await apiClient.get(`/sections/${sectionId}`);
      const sectionData = sectionResponse.data.section;
      setSection(sectionData);
      
      // 2. Obtener grupos de la sección (sistema CourseGroup)
      const groupsResponse = await apiClient.get(`/sections/${sectionId}/groups`);
      const groupList = groupsResponse.data.groups || [];
      setGroups(groupList);
      
      if (groupList.length === 0) {
        setWeekData(null);
        setSubmissionsData([]);
        setLoading(false);
        return;
      }
      
      // 3. Obtener submissions de todos los grupos para esta semana
      // El endpoint también devuelve weekTemplate con los campos B2
      const submissionsPromises = groupList.map(group =>
        apiClient.get(`/student-execution/groups/${group.groupId}/week/${weekNum}`)
          .then(res => {
            const data = res.data.data || {};
            return {
              groupId: group.groupId,
              groupName: group.name,
              submission: data.submission,
              weekTemplate: data.weekTemplate || null,
              members: (group.members || []).map(m => ({
                userId: m._id || m,
                userName: m.nombre || m.name || 'Miembro',
                email: m.email || ''
              }))
            };
          })
          .catch(() => ({
            groupId: group.groupId,
            groupName: group.name,
            submission: null,
            weekTemplate: null,
            members: (group.members || []).map(m => ({
              userId: m._id || m,
              userName: m.nombre || m.name || 'Miembro',
              email: m.email || ''
            }))
          }))
      );
      
      const submissions = await Promise.all(submissionsPromises);
      setSubmissionsData(submissions);
      
      // 4. Usar weekTemplate del primer grupo que lo tenga
      const firstTemplate = submissions.find(s => s.weekTemplate)?.weekTemplate;
      if (firstTemplate) {
        setWeekData(firstTemplate);
      } else if (sectionData?.courseId) {
        // Fallback: intentar con course definition
        try {
          const courseRes = await apiClient.get(`/admin/group-courses/${sectionData.courseId}`);
          const week = courseRes.data?.weeks?.find(w => w.weekNumber === weekNum);
          if (week) setWeekData(week);
        } catch (_) { /* opcional */ }
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error loading week monitor data:', err);
      setError('Error al cargar datos del monitor');
      setLoading(false);
    }
  };
  
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadWeekMonitorData();
    setRefreshing(false);
  };
  
  const handleReopenSubmission = async (submissionId) => {
    if (!confirm('¿Estás seguro de reabrir este entregable? Los estudiantes podrán editarlo nuevamente.')) {
      return;
    }
    
    try {
      await apiClient.put(`/student-execution/teacher/submissions/${submissionId}/reopen`);
      alert('Entregable reabierto exitosamente');
      await handleRefresh();
    } catch (error) {
      console.error('Error reopening submission:', error);
      alert(`Error al reabrir: ${error.response?.data?.message || error.message}`);
    }
  };
  
  const handleViewGroupDetails = async (groupId) => {
    try {
      const groupData = submissionsData.find(s => s.groupId === groupId);
      setGroupDetails(groupData);
      setSelectedGroupId(groupId);
    } catch (error) {
      console.error('Error loading group details:', error);
    }
  };
  
  const handleCloseDetails = () => {
    setSelectedGroupId(null);
    setGroupDetails(null);
  };
  
  // Calcular estadísticas de la semana
  const getWeekStats = () => {
    const total = submissionsData.length;
    const declared = submissionsData.filter(s => s.submission?.status === 'declared').length;
    const inProgress = submissionsData.filter(s => 
      s.submission?.status === 'in_progress' || s.submission?.status === 'ready_for_review'
    ).length;
    const notStarted = submissionsData.filter(s => 
      !s.submission || s.submission?.status === 'not_started'
    ).length;
    
    return { total, declared, inProgress, notStarted };
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-slate-600">Cargando monitor de semana...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="max-w-4xl mx-auto mt-8 p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-red-600" />
            <div>
              <h3 className="font-semibold text-red-900">Error</h3>
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  const stats = getWeekStats();
  
  return (
    <div className="max-w-7xl mx-auto px-4 py-6 md:px-6 md:py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/teacher/${sectionId}/dashboard`)}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Volver al dashboard</span>
        </button>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
              Monitor - Semana {weekNum}
            </h1>
            <p className="text-slate-600 mt-1">
              {section?.name} • {weekData?.title}
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(`/teacher/${sectionId}/settings`)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              title="Configuración de sección"
            >
              <Settings className="w-4 h-4" />
              <span className="hidden md:inline">Configuración</span>
            </button>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="hidden md:inline">Actualizar</span>
            </button>
          </div>
        </div>
      </div>
      
      {/* Estadísticas Generales */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-sl ate-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-blue-500" />
            <div>
              <p className="text-sm text-slate-600">Total Grupos</p>
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-8 h-8 text-green-500" />
            <div>
              <p className="text-sm text-slate-600">Declarados</p>
              <p className="text-2xl font-bold text-green-700">{stats.declared}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Clock className="w-8 h-8 text-blue-500" />
            <div>
              <p className="text-sm text-slate-600">En Progreso</p>
              <p className="text-2xl font-bold text-blue-700">{stats.inProgress}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-8 h-8 text-slate-400" />
            <div>
              <p className="text-sm text-slate-600">No Iniciados</p>
              <p className="text-2xl font-bold text-slate-700">{stats.notStarted}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Tabla de Grupos */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-lg font-semibold text-slate-900">
            Grupos y Progreso
          </h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                  Grupo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                  Miembros
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                  Comentarios
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                  Última Edición
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-600 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {submissionsData.map((groupData) => {
                const submission = groupData.submission;
                const missingComments = submission ? 
                  (submission.commentRequirements?.requiredComments || 0) - (submission.commentRequirements?.completedComments || 0) 
                  : groupData.members?.length || 0;
                
                return (
                  <tr key={groupData.groupId} className="hover:bg-slate-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-slate-900">
                        {groupData.groupName}
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-600">
                          {groupData.members?.length || 0} miembros
                        </span>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      {submission ? (
                        <StatusBadge status={submission.status} size="sm" />
                      ) : (
                        <StatusBadge status="not_started" size="sm" />
                      )}
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      {submission ? (
                        <div className="flex items-center gap-2">
                          <MessageSquare className={`w-4 h-4 ${submission.commentRequirements?.allCommented ? 'text-green-500' : 'text-orange-500'}`} />
                          <span className={`text-sm font-medium ${submission.commentRequirements?.allCommented ? 'text-green-700' : 'text-orange-700'}`}>
                            {submission.commentRequirements?.completedComments || 0}/{submission.commentRequirements?.requiredComments || 0}
                          </span>
                          {missingComments > 0 && (
                            <span className="text-xs text-orange-600">
                              ({missingComments} faltantes)
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-slate-400">-</span>
                      )}
                    </td>
                    
                    <td className="px-6 py-4 max-w-xs">
                      {submission ? (
                        <LastEditInfo 
                          submissionId={submission.submissionId} 
                          display="inline"
                          autoRefresh={false}
                        />
                      ) : (
                        <span className="text-sm text-slate-400">Sin actividad</span>
                      )}
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleViewGroupDetails(groupData.groupId)}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          <span>Ver</span>
                        </button>
                        
                        {submission && (submission.status === 'declared' || submission.status === 'closed') && (
                          <button
                            onClick={() => handleReopenSubmission(submission.submissionId)}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm text-orange-600 hover:bg-orange-50 rounded transition-colors"
                          >
                            <RefreshCw className="w-4 h-4" />
                            <span>Reabrir</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Modal de Detalles del Grupo */}
      {selectedGroupId && groupDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-slate-900">
                {groupDetails.groupName} - Detalles
              </h3>
              <button
                onClick={handleCloseDetails}
                className="text-slate-400 hover:text-slate-600 p-1 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              {groupDetails.submission ? (
                <div className="space-y-6">
                  {/* Estado y metadata */}
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3">Estado del Entregable</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-slate-600 mb-1">Estado Actual</p>
                        <StatusBadge status={groupDetails.submission.status} size="md" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-600 mb-1">Comentarios</p>
                        <div className="flex items-center gap-2">
                          <MessageSquare className="w-5 h-5 text-slate-400" />
                          <span className="font-medium">
                            {groupDetails.submission.commentRequirements?.completedComments || 0}/
                            {groupDetails.submission.commentRequirements?.requiredComments || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Contenido B2 */}
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3">Contenido B2 (Respuestas)</h4>
                    {groupDetails.submission.content?.b2_responses && Object.keys(groupDetails.submission.content.b2_responses).length > 0 ? (
                      <div className="space-y-3">
                        {Object.entries(groupDetails.submission.content.b2_responses).map(([fieldId, value]) => {
                          // Resolver label legible desde la plantilla de la semana
                          const fieldDef = weekData?.b2_execution_template?.fields?.find(f => f.fieldId === fieldId);
                          const label = fieldDef?.label || fieldId;
                          return (
                            <div key={fieldId} className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                              <p className="text-sm font-medium text-slate-700 mb-1">{label}</p>
                              <p className="text-slate-600 whitespace-pre-wrap">{value || '(Sin respuesta)'}</p>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-slate-400">Sin respuestas B2 aún</p>
                    )}
                  </div>
                  
                  {/* Declaración B5 */}
                  {groupDetails.submission.content?.b5_declaration && (
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">Declaración B5</h4>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <p className="text-green-900">{groupDetails.submission.content.b5_declaration.summary}</p>
                        {groupDetails.submission.content.b5_declaration.deliverables?.length > 0 && (
                          <ul className="mt-2 space-y-1">
                            {groupDetails.submission.content.b5_declaration.deliverables.map((item, idx) => (
                              <li key={idx} className="text-sm text-green-700">• {item}</li>
                            ))}
                          </ul>
                        )}
                        <p className="text-xs text-green-600 mt-2">
                          Declarado el {new Date(groupDetails.submission.content.b5_declaration.completedAt).toLocaleString('es-ES')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-slate-400 text-center py-8">
                  Este grupo aún no ha iniciado la semana {weekNum}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherMonitorWeekPage;
